
import ImageDropzone from "@/components/base/input/dropzone/ImageDropzone"
import FileInput from "@/components/base/input/FileInput"
import UploadPreview from "@/components/base/image/UploadPreview"
import Button from "@/components/base/Button"
import Cropper from "@/components/base/cropper/Cropper"

import imageService from "@/services/image.service"
import { mdiPlus, mdiDelete, mdiCrop } from "@mdi/js"
import { IMAGE_TYPES } from "@/enums/fileTypes"

export default {
    name: "ImageUploader",
    emits: ["update:image", "uploaded"],
    components: {
        ImageDropzone,
        UploadPreview,
        Button,
        FileInput,
        Cropper,
        Modal: () => import("@/components/base/modal/Modal"),
    },
    props: {
        aspectRatio: {
            type: Number,
            default: 1,
        },
        image: {
            type: [String, Object],
            default: null,
        },
        acceptedFormats: {
            type: Array,
            default: () => IMAGE_TYPES,
        },
        size: {
            type: String,
            default: "200_fit",
        },
        round: {
            type: Boolean,
            default: false,
        },
        fit: {
            type: String,
            default: "contain",
        },
        /** Opens cropper in modal */
        cropperModal: {
            type: Boolean,
            default: false,
        },
        /** Enable/disable dropzone drag & drop for the DropzoneBase */
        fileDropping: {
            type: Boolean,
            default: true,
        },
    },
    icons: {
        mdiPlus,
        mdiDelete,
        mdiCrop,
    },
    data: () => ({
        isCropping: false,
    }),
    computed: {
        internalImage: {
            get() {
                return this.image
            },
            set(newVal) {
                this.$emit("update:image", newVal)
            },
        },
        error() {
            if (this.image?.request?.state === "error") {
                return this.image.request.error
            }
            return false
        },
        imageSrc() {
            if (!this.internalImage) return
            if (this.internalImage.local) return this.internalImage.local
            const isFileEmpty =
                this.internalImage &&
                Object.keys(this.internalImage).length === 0 &&
                Object.getPrototypeOf(this.internalImage) === Object.prototype
            if (!isFileEmpty) {
                // imgproxy (pre-rendered legacy sizes are no longer kept)
                if (this.internalImage.urls?.[`${this.size}.jpg`]) {
                    return this.internalImage.urls[`${this.size}.jpg`]
                }
                // without urls: legacy path, redirected to imgproxy by the API
                return (
                    this.internalImage.file_path +
                    this.internalImage.file_name +
                    `_${this.size}.jpg`
                )
            }
            return this.internalImage
        },
        showUploadPreview() {
            if (!this.internalImage) return false
            if (this.cropperModal) return true
            return !(this.imageSrc && this.isCropping)
        },
    },
    methods: {
        onCrop(base64) {
            const img = imageService.asClass(this.internalImage)
            img.update(base64)
            this.internalImage = img
            this.isCropping = false
        },
        onReupload() {
            this.internalImage.create().then(() => {
                this.$emit("uploaded", this.internalImage)
            })
        },
        /** file: File object or base64(from cropper) */
        async uploadImage(file) {
            const image = imageService.asClass(file)
            image.create().then(() => {
                this.$emit("uploaded", image)
            })
            this.internalImage = image
        },
        onDelete() {
            imageService.asClass(this.internalImage).cancel()
            this.internalImage = null
        },
        onFileSelect(file) {
            this.$refs.imageDropzone.receiveImages([file])
        },
    },
}
